import { useState, useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Grid, Tooltip } from '@mui/material'
import BlockIcon from '@mui/icons-material/Block'
import CheckIcon from '@mui/icons-material/Check'
import ClearIcon from '@mui/icons-material/Clear'
import AccessTimeIcon from '@mui/icons-material/AccessTime'

import Button from './Button'
import Dialog from './Dialog'
import AutoComplete from './AutoComplete'
import GridCheckbox from './GridCheckbox'
import GridCircularProgress from '../components/GridCircularProgress'
import { colours, GIORNI, titlesDesignazioni } from '../settings/settings'
import { sortChronologically } from '../hooks/sort-functions'
import {
  useGetAffiliazioniLightQuery,
  useGetArbitriQuery,
  useGetArbitriLightQuery,
  useGetCalendariRefereeDataQuery,
  useGetDesignazioniByGaraQuery,
  useGetDesignazioniByGaraOtherQuery,
  useAnnullaDesignazioneMutation,
  useGetIscrizioniQuery,
  useGetIscrizioneByIdQuery,
} from '../hooks/api-slice'
import {
  fromStringsToDateTime,
  fromStringToDecimalHours,
  isObjectId,
  useTeamName,
  useIndirizzoGara,
} from '../hooks/utils-functions'
import {
  resetDesignazione,
  setSelectedArbitro1,
  setSelectedArbitro2,
  setCheckAccept,
  setCheckReject,
} from '../container/designazione'

const groupsOrder = [
  'Disponibili',
  'Fuori disponibilità settimanale',
  'Società incompatibile',
  'Altra designazione',
  'Indisponibili',
]

const DialogDesignazione = ({
  open,
  handleClose,
  triggerFunction,
  designazioni,
  mode,
}) => {
  const infoUtente = useSelector(state => state.home.infoUtente)
  const { admin, arbitro, designatore } = infoUtente
  const selectedStagione = useSelector(
    state => state.selection.selectedStagione,
  )
  const selectedCampionato = useSelector(
    state => state.selection.selectedCampionato,
  )

  const { data: iscrizioniNomi } = useGetIscrizioniQuery(
    { campionato: selectedCampionato, iFilter: 'nomi' },
    { skip: !((admin || designatore) && selectedCampionato) },
  )
  const { data: affiliazioniLight } = useGetAffiliazioniLightQuery(undefined, {
    skip: !(admin || designatore),
  })
  const { data: arbitriLight } = useGetArbitriLightQuery(undefined, {
    skip: !arbitro,
  })
  const { data: arbitri } = useGetArbitriQuery(undefined, {
    skip: !(admin || designatore),
  })
  const designazione = useSelector(state => state.designazione.designazione)
  const selectedGara = useSelector(state => state.designazione.selectedGara)
  const selectedArbitro1 = useSelector(
    state => state.designazione.selectedArbitro1,
  )
  const selectedArbitro2 = useSelector(
    state => state.designazione.selectedArbitro2,
  )
  const checkAccept = useSelector(state => state.designazione.checkAccept)
  const checkReject = useSelector(state => state.designazione.checkReject)

  const ss = arbitro ? selectedStagione : undefined
  const squadraCasaName = useTeamName(selectedGara?.squadraCasaID, ss)
  const squadraTrasfertaName = useTeamName(selectedGara?.squadraTrasfertaID, ss)
  const indirizzoGara = useIndirizzoGara(selectedGara)

  const { isFetching: isFetchingInd } = useGetIscrizioneByIdQuery(
    selectedGara?.squadraCasaID,
    { skip: !isObjectId(selectedGara?.squadraCasaID) },
  )

  const { data: orariGareOdierne } = useGetCalendariRefereeDataQuery(
    { data: selectedGara?.data },
    { skip: !((admin || designatore) && selectedGara?.data) },
  )

  const { data: garaDesignazioni } = useGetDesignazioniByGaraQuery(
    selectedGara?._id,
    { skip: !((admin || designatore) && selectedGara?._id) },
  )

  const garaDesignazioni1 = garaDesignazioni
    ?.filter(x => !x.isSecondoArbitro)
    ?.sort((a, b) => -sortChronologically(a.timeDomanda, b.timeDomanda))

  const garaDesignazioni2 = garaDesignazioni
    ?.filter(x => x.isSecondoArbitro)
    ?.sort((a, b) => -sortChronologically(a.timeDomanda, b.timeDomanda))

  const { data: otherDesignazione, isFetching: isFetchingOther } =
    useGetDesignazioniByGaraOtherQuery(
      { idGara: selectedGara?._id, idArbitro: designazione?.idArbitro },
      {
        skip: !(
          arbitro &&
          selectedGara?._id &&
          designazione?.idArbitro &&
          designazione?.isValid === true
        ),
      },
    )

  const [loading1, setLoading1] = useState()
  const [loading2, setLoading2] = useState()
  const [annullaDesignazioneMutation] = useAnnullaDesignazioneMutation()

  const dispatch = useDispatch()

  const [matchDay, setMatchDay] = useState()
  const [matchTime, setMatchTime] = useState()
  const [codiciSocietaGara, setCodiciSocietaGara] = useState()
  const [otherArbitro, setOtherArbitro] = useState()

  // map designazione stato
  const desStatus = designazione =>
    designazione?.isValid === false
      ? designazione?.acceptedByArbitro === false
        ? 'Rifiutata'
        : 'Annullata'
      : designazione?.acceptedByArbitro === true
        ? 'Accettata'
        : 'In sospeso'

  // update match date and time
  useEffect(() => {
    if (selectedGara) {
      setMatchDay(
        fromStringsToDateTime(selectedGara?.data, selectedGara?.ora)?.getDay(),
      )
      setMatchTime(fromStringToDecimalHours(selectedGara?.ora))
    }
  }, [selectedGara])

  // update codiciSocieta gara
  useEffect(() => {
    if (selectedGara) {
      setCodiciSocietaGara(
        iscrizioniNomi
          ?.filter(i =>
            [
              selectedGara?.squadraCasaID,
              selectedGara?.squadraTrasfertaID,
            ]?.includes(i.id),
          )
          ?.map(i => i.codiceSocieta),
      )
    }
  }, [iscrizioniNomi, selectedGara])

  // update other arbitro
  useEffect(() => {
    if (arbitriLight && otherDesignazione)
      setOtherArbitro(
        arbitriLight?.find(a => a.id === otherDesignazione?.idArbitro),
      )
  }, [arbitriLight, otherDesignazione])

  // filter, group and sort arbitri
  const fArbitri =
    (selectedGara &&
      arbitri &&
      designazioni &&
      affiliazioniLight &&
      GIORNI &&
      groupsOrder &&
      orariGareOdierne &&
      codiciSocietaGara &&
      typeof matchDay === 'number' &&
      typeof matchTime === 'number' &&
      arbitri
        ?.filter(a => a?.isActive)
        ?.map(a => {
          const dispSettimanale = a?.disponibilitaSettimanali?.some(
            d =>
              (matchDay - 1 + GIORNI.length) % GIORNI.length ===
                GIORNI.findIndex(g => g === d?.giorno) &&
              matchTime >= fromStringToDecimalHours(d?.dalle) &&
              matchTime <= fromStringToDecimalHours(d?.alle),
          )
          const altreDesignazioni = orariGareOdierne
            ?.filter(o =>
              designazioni
                ?.filter(d => d.isValid && d.idArbitro === a.id)
                ?.map(d => d.idGara)
                ?.includes(o.id),
            )
            ?.some(
              o =>
                Math.abs(
                  fromStringToDecimalHours(o.ora) -
                    fromStringToDecimalHours(selectedGara?.ora),
                ) <= 3,
            )
          const socIncompatibili = a?.codiciSocietaIncompatibili
            ?.map(s => affiliazioniLight?.find(i => i.id === s)?.codiceSocieta)
            ?.some(s => codiciSocietaGara?.includes(s))
          const indispPuntuali = a?.indisponibilitaPuntuali?.some(
            i => i === selectedGara?.data,
          )
          const aDomicilio = a?.cittaDomicilio ? ` (${a.cittaDomicilio})` : ''
          return {
            ...a,
            gruppo: indispPuntuali
              ? 'Indisponibili'
              : altreDesignazioni
                ? 'Altra designazione'
                : socIncompatibili
                  ? 'Società incompatibile'
                  : !dispSettimanale
                    ? 'Fuori disponibilità settimanale'
                    : 'Disponibili',
            label: `${a?.nome} ${a?.cognome}${aDomicilio}`,
          }
        })
        ?.sort(
          (a, b) =>
            groupsOrder.indexOf(a.gruppo) - groupsOrder.indexOf(b.gruppo) ||
            (a.cognome > b.cognome ? 1 : -1),
        )) ||
    []

  useEffect(() => {
    if (!open) dispatch(resetDesignazione())
  }, [dispatch, open])

  const GridElement = ({ label, field, mapping }) => {
    return (
      <Grid size={{ xs: 12, sm: 6, md: 3 }}>
        <center>
          {label}&nbsp;
          <b>
            {mapping
              ? mapping(selectedGara?.[field])
              : selectedGara?.[field] ||
                (field === 'data' && 'non definita') ||
                (field === 'ora' && 'non definito') ||
                '?'}
          </b>
        </center>
      </Grid>
    )
  }

  const ListDesignazioni = ({ n, garaDesignazioni }) => {
    if (!garaDesignazioni?.length)
      return (
        <Grid>
          <center>
            <p style={{ color: colours.grey }}>
              Nessun {n}° arbitro designato.
            </p>
          </center>
        </Grid>
      )

    const loading = (n === 1 && loading1) || (n === 2 && loading2) || false
    const setLoading =
      (n === 1 && setLoading1) || (n === 2 && setLoading2) || (() => {})

    return garaDesignazioni.map(x => {
      const xArbitro = arbitri?.find(a => a.id === x?.idArbitro)
      const isValid = x?.isValid === true
      const isAccepted = x?.acceptedByArbitro === true
      const isRejected = x?.acceptedByArbitro === false
      const isPending = ![true, false].includes(x?.acceptedByArbitro)
      return (
        <Grid key={x.id} container alignItems='center' sx={{ width: '100%' }}>
          <Grid paddingX={2}>
            <center>
              {isAccepted && (
                <Tooltip title='Accettata'>
                  <CheckIcon color='success' />
                </Tooltip>
              )}
              {isRejected && (
                <Tooltip title='Rifiutata'>
                  <ClearIcon color='error' />
                </Tooltip>
              )}
              {isValid && isPending && (
                <Tooltip title='In sospeso'>
                  <AccessTimeIcon color='warning' />
                </Tooltip>
              )}
              &nbsp;
              {!isValid && !isRejected && (
                <Tooltip title='Annullata dal designatore'>
                  <BlockIcon color='error' />
                </Tooltip>
              )}
            </center>
          </Grid>
          <Grid>
            {xArbitro?.nome && xArbitro?.cognome ? (
              <p>
                {xArbitro.nome} {xArbitro.cognome}
              </p>
            ) : (
              <i>Nome non trovato</i>
            )}
          </Grid>
          {isValid && (
            <Grid size='grow' paddingX={2} sx={{ textAlign: 'right' }}>
              <Button
                error
                loading={loading}
                onClick={async () => {
                  setLoading(true)
                  const body = { idDesignazione: x._id, sendMail: true }
                  await annullaDesignazioneMutation(body)
                  setLoading()
                }}
                children='Annulla'
              />
            </Grid>
          )}
        </Grid>
      )
    })
  }

  return (
    <Dialog
      open={open}
      handleClose={handleClose}
      triggerFunction={triggerFunction}
      title={
        ((admin || designatore) && titlesDesignazioni.titleDesignazione) ||
        (designazione?.isSecondoArbitro &&
          titlesDesignazioni.titleDesignazione2) ||
        titlesDesignazioni.titleDesignazione1
      }
      textConfirm={
        ((admin || designatore) && 'Designa') ||
        (mode === 'reply' && 'Conferma') ||
        ''
      }
      maxWidth='lg'
    >
      {((isFetchingInd || isFetchingOther) && <GridCircularProgress />) ||
        (!selectedGara && <Grid size={12}>Gara non trovata.</Grid>) || (
          <Grid
            container
            spacing={2}
            alignItems='center'
            sx={{ width: '100%' }}
          >
            {arbitro && mode === 'reply' && (
              <Grid size={12}>
                Il presente modulo permette di accettare o rifiutare la
                designazione. La compilazione del modulo genera una mail
                automatica al designatore.
              </Grid>
            )}
            <GridElement label='Gara' field='numeroGara' />
            <GridElement label='Categoria' field='categoria' />
            <GridElement label='Data' field='data' />
            <GridElement label='Orario' field='ora' />
            <Grid size={12}>
              <center>
                <b>{squadraCasaName}</b>
                &nbsp;vs&nbsp;
                <b>{squadraTrasfertaName}</b>
              </center>
            </Grid>
            <Grid size={12}>
              <center>Campo di gioco:&nbsp;{indirizzoGara}</center>
            </Grid>
            {arbitro && otherDesignazione && otherArbitro && (
              <Grid size={12}>
                <center>
                  {otherDesignazione?.isSecondoArbitro ? '2° ' : '1° '}
                  arbitro&nbsp;
                  <b>
                    {otherArbitro?.nome} {otherArbitro?.cognome}
                  </b>
                  &nbsp;({desStatus(otherDesignazione)})
                </center>
              </Grid>
            )}
            {(admin || designatore) && mode === 'update' && (
              <Grid
                container
                paddingY={3}
                columnSpacing={3}
                alignItems='flex-start'
                sx={{ width: '100%' }}
              >
                <Grid size={{ xs: 12, md: 6 }}>
                  <Grid container sx={{ width: '100%' }}>
                    <Grid size={12}>
                      <AutoComplete
                        id='nuovo-arbitro-1'
                        label='Nuovo 1° arbitro'
                        value={selectedArbitro1}
                        options={fArbitri}
                        groupBy={op => op?.gruppo}
                        getOptionLabel={op => op?.label}
                        onChange={value => dispatch(setSelectedArbitro1(value))}
                      />
                    </Grid>
                    <ListDesignazioni
                      n={1}
                      garaDesignazioni={garaDesignazioni1}
                    />
                  </Grid>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Grid container sx={{ width: '100%' }}>
                    <Grid size={12}>
                      <AutoComplete
                        id='nuovo-arbitro-2'
                        label='Nuovo 2° arbitro'
                        value={selectedArbitro2}
                        options={fArbitri}
                        groupBy={op => op?.gruppo}
                        getOptionLabel={op => op?.label}
                        onChange={value => dispatch(setSelectedArbitro2(value))}
                      />
                    </Grid>
                    <ListDesignazioni
                      n={2}
                      garaDesignazioni={garaDesignazioni2}
                    />
                  </Grid>
                </Grid>
              </Grid>
            )}
            {arbitro && mode === 'view' && (
              <Grid size={12}>
                <center>
                  Stato&nbsp;
                  <b>{desStatus(designazione)}</b>
                </center>
              </Grid>
            )}
            {arbitro && mode === 'reply' && (
              <>
                <GridCheckbox
                  label='Accetto la designazione.'
                  value={!!checkAccept}
                  onChange={(_, x) => {
                    dispatch(setCheckAccept(x))
                    if (!!x) dispatch(setCheckReject(!x))
                  }}
                />
                <GridCheckbox
                  label='Rifiuto la designazione.'
                  value={!!checkReject}
                  onChange={(_, x) => {
                    dispatch(setCheckReject(x))
                    if (!!x) dispatch(setCheckAccept(!x))
                  }}
                />
              </>
            )}
          </Grid>
        )}
    </Dialog>
  )
}

export default DialogDesignazione
